import { CDN_URL } from "@/consts";
import { PURCHASE_PLANS, TRACKER_PACK_ENTRY, money } from "@/consts/pricing";

export type FeatureFaq = {
  question: string;
  answer: string;
};

export type FeatureContent = {
  related?: string[];
  title: string;
  trustedBy?: string;
  answer: string;
  description: string;
  heroPrefix: string;
  heroHighlight: string;
  heroSuffix: string;
  heroImage?: string;
  heroImageFlare?: string;
  stepImages?: string[];
  buttonText: string;
  buttonLink: string;
  seoTitle: string;
  seoDescription: string;
  faqs?: FeatureFaq[];
};

export const FEATURE_CONTENT_MAP: Record<string, FeatureContent> = {
  "ai-image": {
    related: [
      "/features/icon-generator",
      "/features/remove-bg",
      "/use-cases/faceless-youtube-channels",
    ],
    title: "AI Images",
    answer:
      "The Crayo AI Image tool generates an image from a text prompt, using a built-in prompt enhancer to sharpen the result, and returns the image in seconds. It is built for faceless YouTube channel creators and other short-form producers who need visuals or thumbnails without a designer.",
    description:
      "Effortlessly create AI images in seconds, use our prompt enhancer to get the best results.",
    heroPrefix: "Generate",
    heroHighlight: "Stunning Images",
    heroSuffix: "with AI",
    heroImage: `${CDN_URL}/features/ai-image/ai-image-hero.png`,
    stepImages: [
      `${CDN_URL}/features/ai-image/ai-image-step1.png`,
      `${CDN_URL}/features/ai-image/ai-image-step2.png`,
      `${CDN_URL}/features/ai-image/ai-image-step3.png`,
    ],
    buttonText: "Create Now",
    buttonLink: "/tools/images",
    seoTitle: "AI Image Generator",
    seoDescription:
      "Describe what you need, and our AI will generate high-quality, unique images in seconds perfect for ads, content, or creative projects.",
  },
  "ai-voiceover": {
    related: [
      "/features/voice-changer",
      "/features/speech-enhancer",
      "/use-cases/faceless-youtube-channels",
    ],
    title: "AI Voiceover",
    answer:
      "The AI Voiceover tool converts a written script into spoken narration, using more than 100 AI voices to produce audio for a video in seconds. It is for faceless YouTube channel and brainrot-video creators, plus agencies, who need narration without recording their own voice.",
    description:
      "Use any of our 100+ high-quality AI voices to create engaging voiceovers for your videos.",
    heroPrefix: "Generate",
    heroHighlight: "AI Voiceovers",
    heroSuffix: "instantly",
    heroImage: `${CDN_URL}/features/ai-voiceover/ai-voiceover-hero.png`,
    stepImages: [
      `${CDN_URL}/features/ai-voiceover/ai-voiceover-step1.png`,
      `${CDN_URL}/features/ai-voiceover/ai-voiceover-step2.png`,
      `${CDN_URL}/features/ai-voiceover/ai-voiceover-step3.png`,
    ],
    buttonText: "Create Now",
    buttonLink: "/tools/voiceovers",
    seoTitle: "AI Voiceover Generator",
    seoDescription:
      "Turn your scripts into studio-quality audio using our wide range of natural-sounding AI voices perfect for ads, explainer videos, podcasts, and more.",
  },
  "speech-enhancer": {
    related: ["/features/ai-voiceover", "/features/voice-changer", "/use-cases/podcast-clipping"],
    title: "Speech Enhancer",
    answer:
      "Speech Enhancer takes an uploaded audio file recorded in poor conditions, such as with echo or background noise, and processes it with AI into cleaner, professional-sounding audio. It suits podcasters, streamers, and agencies cleaning up recordings before clipping or publishing.",
    description:
      "Upload an audio file recorded in poor conditions and let our AI enhance it to sound professional.",
    heroPrefix: "Enhance speech in any audio file",
    heroHighlight: "",
    heroSuffix: "",
    heroImage: `${CDN_URL}/features/speech-enhancer/speech-enhancer-hero.png`,
    stepImages: [
      `${CDN_URL}/features/speech-enhancer/speech-enhancer-step1.png`,
      `${CDN_URL}/features/speech-enhancer/speech-enhancer-step2.png`,
      `${CDN_URL}/features/speech-enhancer/speech-enhancer-step3.png`,
    ],
    buttonText: "Create Now",
    buttonLink: "/tools/speech-enhancer",
    seoTitle: "AI Speech Enhancer",
    seoDescription:
      "Clean up echo, background noise, and uneven levels with AI. Turn rough recordings into professional-sounding audio in seconds.",
  },
  "remove-bg": {
    related: ["/features/ai-image", "/features/face-swap", "/features/video-crop"],
    title: "Remove Background",
    answer:
      "Remove Background accepts an uploaded image or video and uses AI to strip out the background in seconds, without a green screen or manual masking. It is for creators and marketers who need cutouts for thumbnails, product shots, or overlay footage.",
    description: "Upload any image or video and remove the background in seconds with AI.",
    heroPrefix: "Video & Image",
    heroHighlight: "Background Removal",
    heroSuffix: "",
    heroImage: `${CDN_URL}/features/remove-bg/remove-bg-hero.png`,
    stepImages: [
      `${CDN_URL}/features/remove-bg/remove-bg-step1.png`,
      `${CDN_URL}/features/remove-bg/remove-bg-step2.png`,
      `${CDN_URL}/features/remove-bg/remove-bg-step3.png`,
    ],
    buttonText: "Upload video",
    buttonLink: "/tools/remove-bg",
    seoTitle: "Video and Image Background Remover",
    seoDescription:
      "Remove backgrounds from videos and images in seconds with AI. No green screen, no manual masking, just clean cutouts.",
  },
  "fake-texts": {
    related: [
      "/use-cases/brainrot-channels",
      "/features/ai-voiceover",
      "/use-cases/faceless-youtube-channels",
      "/compare/crayo-vs-clippie",
      "/compare/crayo-vs-vsub",
      "/compare/crayo-vs-sendshort",
    ],
    title: "Fake Text Chat",
    answer:
      "Fake Text Chat is a template generator that assembles a scripted phone-conversation video, using AI to help write the story and reveal it message by message. It is for faceless YouTube channel and brainrot-video creators making story-driven short-form content.",
    description:
      "Use our easy-to-use template generator packed with AI features to assemble your story",
    heroPrefix: "Make",
    heroHighlight: "Viral Fake Text Stories",
    heroSuffix: "In Seconds",
    heroImage: `${CDN_URL}/features/fake-texts/fake-texts-hero.png`,
    stepImages: [
      `${CDN_URL}/features/fake-texts/fake-texts-step1.png`,
      `${CDN_URL}/features/fake-texts/fake-texts-step2.png`,
      `${CDN_URL}/features/fake-texts/fake-texts-step3.png`,
    ],
    buttonText: "Create Now",
    buttonLink: "/create/text-video",
    seoTitle: "Fake Text Story Video Generator",
    seoDescription:
      "Create viral fake text story videos in a few clicks. Write or generate the conversation, pick the voices, and export a captioned vertical video.",
  },
  "reddit-story": {
    title: "Reddit Story",
    answer:
      "Reddit Story turns a pasted or written story into a narrated video with an AI voiceover and captions, generated in a few clicks. It is for faceless YouTube channel and brainrot-video creators who want story-driven content without filming.",
    description: "Create viral Reddit story videos with just a few clicks.",
    heroPrefix: "Make",
    heroHighlight: "Viral Reddit Stories",
    heroSuffix: "In Seconds",
    heroImage: `${CDN_URL}/features/reddit-story/reddit-story-hero.png`,
    heroImageFlare: `${CDN_URL}/features/reddit-story/reddit-icon.png`,
    stepImages: [
      `${CDN_URL}/features/reddit-story/reddit-story-step1.png`,
      `${CDN_URL}/features/reddit-story/reddit-story-step2.png`,
      `${CDN_URL}/features/reddit-story/reddit-story-step3.png`,
    ],
    buttonText: "Create Now",
    buttonLink: "/create/reddit-video",
    seoTitle: "Reddit Story Video Generator",
    seoDescription:
      "Create viral Reddit story videos in a few clicks. Paste a post or write your own, add an AI voiceover and captions, and export it ready to publish.",
  },
  "auto-clip": {
    related: [
      "/use-cases/clippers",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
      "/compare/crayo-vs-klap",
    ],
    title: "Auto Clip",
    answer:
      "Auto Clip takes an uploaded long-form video, such as a podcast, stream, or webinar, and uses AI to find and cut the strongest moments into short, captioned clips. It is for clippers, podcasters, streamers, and agencies repurposing long recordings into short-form video at volume.",
    description:
      "Upload a long-form video and let AI find the best moments, then cut them into ready-to-post clips.",
    heroPrefix: "Turn long videos into",
    heroHighlight: "viral clips",
    heroSuffix: "automatically",
    heroImage: "/features/auto-clip/auto-clip-hero.png",
    buttonText: "Clip a Video Now",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Auto Clipper",
    seoDescription:
      "Upload podcasts, streams, or any long-form video and let AI cut the best moments into viral short-form clips in seconds.",
    faqs: [
      {
        question: "How does Auto Clip pick the moments?",
        answer:
          "AI scans the full video for hooks, such as stories, punchlines, and high-energy segments, and scores each clip so you can post the strongest ones first.",
      },
      {
        question: "How long can my upload be?",
        answer:
          "Long-form is the point: podcasts, streams, and webinars work great. Longer videos just take a little more processing time.",
      },
      {
        question: "Do clips come with subtitles?",
        answer:
          "Yes. Every clip gets styled, accurate captions automatically, and you can tweak the look before exporting.",
      },
      {
        question: "Can I edit the clips before posting?",
        answer:
          "Yes. Open any clip in the editor to trim, reframe, or restyle captions before you export.",
      },
    ],
  },
  "video-cutter": {
    related: ["/features/free/video-trimmer", "/features/video-crop", "/use-cases/clippers"],
    title: "Video Cutter",
    answer:
      "Video Cutter trims or cuts an uploaded video down to a chosen moment entirely in the browser, exporting a clean MP4 with no watermark. It is for clippers, streamers, and agencies who need frame-accurate cuts without installing editing software.",
    description:
      "Trim and cut any video down to the exact moment you need, right in your browser with no watermark.",
    heroPrefix: "Cut videos",
    heroHighlight: "frame-perfect",
    heroSuffix: "right in your browser",
    heroImage: "/features/video-cutter/video-cutter-hero.png",
    buttonText: "Cut a Video Now",
    buttonLink: "/cut-and-crop",
    seoTitle: "Online Video Cutter",
    seoDescription:
      "Trim and cut videos online in seconds. Frame-perfect cutting in your browser with no watermark and no software to install.",
    faqs: [
      {
        question: "Is there a watermark?",
        answer: "No. Exports are clean on every plan.",
      },
      {
        question: "Do I need to install anything?",
        answer: "No. Cutting runs entirely in your browser: upload, trim, export.",
      },
      {
        question: "Will cutting reduce my video quality?",
        answer: "No. Your video keeps its original resolution through the cut.",
      },
      {
        question: "Can I cut multiple sections from one video?",
        answer:
          "Yes. Split the timeline as many times as you need and keep only the parts you want.",
      },
    ],
  },
  "video-crop": {
    related: [
      "/features/free/aspect-ratio-converter",
      "/features/video-cutter",
      "/use-cases/streamers",
    ],
    title: "Video Crop",
    answer:
      "Video Crop reframes an uploaded video into a vertical or square aspect ratio, using presets for TikTok, Reels, and Shorts or a custom crop. It is for clippers, podcast clippers, streamers, and agencies preparing horizontal footage for short-form platforms.",
    description:
      "Reframe any video for TikTok, Reels, or Shorts with precise cropping presets for every platform.",
    heroPrefix: "Crop videos for",
    heroHighlight: "TikTok, Reels & Shorts",
    heroSuffix: "",
    heroImage: "/features/video-crop/video-crop-hero.png",
    buttonText: "Crop a Video Now",
    buttonLink: "/cut-and-crop",
    seoTitle: "Online Video Cropper",
    seoDescription:
      "Crop and reframe videos for TikTok, Reels, and Shorts in seconds. Platform presets, no watermark, all in your browser.",
    faqs: [
      {
        question: "Which aspect ratios are supported?",
        answer:
          "9:16 for TikTok, Reels, and Shorts, plus 1:1, 4:5, and 16:9 presets, or set a custom crop.",
      },
      {
        question: "Is there a watermark?",
        answer: "No. Exports are clean on every plan.",
      },
      {
        question: "Can I reposition the crop?",
        answer:
          "Yes. Drag the frame to keep your subject centered, with a rule-of-thirds grid to guide you.",
      },
      {
        question: "Does cropping lower the quality?",
        answer: "No. The crop exports at the highest resolution your source allows.",
      },
    ],
  },
  "voice-changer": {
    related: [
      "/features/ai-voiceover",
      "/features/speech-enhancer",
      "/features/free/volume-booster",
    ],
    title: "Voice Changer",
    answer:
      "Voice Changer records or accepts uploaded audio and re-renders it in one of more than 100 AI voices while keeping the original pacing and emotion. It is for creators who want to change or disguise a voice in narration or existing recordings.",
    description:
      "Record or upload audio and transform it into any of our 100+ AI voices while keeping your delivery.",
    heroPrefix: "Change your voice with",
    heroHighlight: "100+ AI voices",
    heroSuffix: "",
    heroImage: "/features/voice-changer/voice-changer-hero.png",
    buttonText: "Change Your Voice",
    buttonLink: "/tools/voice-changer",
    seoTitle: "AI Voice Changer",
    seoDescription:
      "Transform any recording into a different voice with AI. Keep your pacing and emotion, swap the voice in seconds.",
    faqs: [
      {
        question: "How does the voice changer work?",
        answer:
          "Record or upload audio, pick one of 100+ AI voices, and Crayo re-renders your words in that voice while keeping your pacing and emotion.",
      },
      {
        question: "Can I use it on existing recordings?",
        answer:
          "Yes. Upload any audio file, from voice memos to podcast tracks to video audio, and convert it.",
      },
      {
        question: "Do the voices sound natural?",
        answer:
          "They're the same studio-quality voices behind Crayo's voiceovers, built for narration people actually listen to.",
      },
      {
        question: "Can I use the output commercially?",
        answer: "Yes. Audio you create in Crayo is yours to use in your content.",
      },
    ],
  },
  "subtitle-remover": {
    related: ["/features/auto-clip", "/features/video-crop", "/use-cases/agencies"],
    title: "Subtitle Remover",
    answer:
      "Subtitle Remover analyzes an uploaded video, detects burned-in captions, and uses AI to reconstruct the footage behind them frame by frame. It is for agencies and editors who need to repurpose clips with hardcoded captions into clean footage for re-captioning.",
    description:
      "Erase burned-in captions from any video with AI so you can repurpose clips with clean footage.",
    heroPrefix: "Remove",
    heroHighlight: "burned-in subtitles",
    heroSuffix: "from any video",
    heroImage: "/features/subtitle-remover/subtitle-remover-hero.png",
    buttonText: "Remove Subtitles Now",
    buttonLink: "/tools/subtitle-remover",
    seoTitle: "AI Subtitle Remover",
    seoDescription:
      "Erase hardcoded captions from any video with AI. Get clean footage back in seconds, ready to re-caption and repost.",
    faqs: [
      {
        question: "How does it remove burned-in subtitles?",
        answer:
          "AI detects the caption area and reconstructs the footage behind it, frame by frame.",
      },
      {
        question: "Will there be smudges where the text was?",
        answer:
          "The model rebuilds the background rather than blurring it, so results stay clean on most footage.",
      },
      {
        question: "What videos does it work on?",
        answer:
          "Any video with hardcoded captions, whether repurposed clips, old exports, or footage you have the rights to reuse.",
      },
      {
        question: "Can I add new captions after?",
        answer:
          "Yes. Run the clean video through Crayo's subtitles to re-caption it in your own style.",
      },
    ],
  },
  "face-swap": {
    related: ["/features/ai-image", "/features/remove-bg", "/features/icon-generator"],
    title: "Face Swap",
    answer:
      "Face Swap accepts an uploaded image or video plus a reference face and maps the new face onto it with AI that matches lighting and angle. It is for creators making content who have permission to use the faces involved, since impersonation violates the terms of service.",
    description:
      "Swap faces in images and videos with one click, powered by AI that keeps lighting and angles natural.",
    heroPrefix: "Swap faces in",
    heroHighlight: "any video or image",
    heroSuffix: "",
    heroImage: "/features/face-swap/face-swap-hero.png",
    buttonText: "Swap a Face Now",
    buttonLink: "/tools/face-swap",
    seoTitle: "AI Face Swap",
    seoDescription:
      "Swap faces in videos and images with one click. AI keeps lighting and angles natural for seamless results.",
    faqs: [
      {
        question: "How do I swap a face?",
        answer:
          "Upload your video or image, add the face you want, and Crayo maps it in one click.",
      },
      {
        question: "Does it work on video, or just images?",
        answer: "Both. Faces track across video frames with lighting and angles preserved.",
      },
      {
        question: "How realistic are the results?",
        answer:
          "The AI matches lighting, skin tone, and head angles, so swaps look natural instead of pasted on.",
      },
      {
        question: "What content is allowed?",
        answer:
          "Only swap faces you have permission to use. Impersonation and misleading content violate our terms.",
      },
    ],
  },
  "youtube-downloader": {
    related: ["/features/auto-clip", "/features/free/video-trimmer", "/use-cases/podcast-clipping"],
    title: "YouTube Downloader",
    answer:
      "YouTube Downloader accepts a pasted YouTube link and downloads the video, ready to bring into Auto Clip or the editor. It is for clippers and podcast clippers sourcing footage they have the rights to repurpose.",
    description:
      "Paste any YouTube link and download the video in high quality, ready to clip and repurpose.",
    heroPrefix: "Download",
    heroHighlight: "YouTube videos",
    heroSuffix: "in full quality",
    heroImage: "/features/youtube-downloader/youtube-downloader-hero.png",
    buttonText: "Download a Video",
    buttonLink: "/tools/youtube-downloader",
    seoTitle: "YouTube Video Downloader",
    seoDescription:
      "Paste a YouTube link and download the video in high quality, ready to clip, edit, and repurpose for short-form.",
    faqs: [
      {
        question: "How do I download a YouTube video?",
        answer: "Paste the link, pick a quality, and download, with no software or extensions.",
      },
      {
        question: "What quality can I download in?",
        answer: "Up to the source's maximum resolution, including 1080p and 4K where available.",
      },
      {
        question: "Can I send the video straight into Crayo?",
        answer:
          "Yes. Downloaded videos can go directly into Auto Clip or the editor for repurposing.",
      },
      {
        question: "Is it legal to download YouTube videos?",
        answer:
          "Download only content you own or have permission to use. Respecting YouTube's terms is on you.",
      },
    ],
  },
  "icon-generator": {
    related: ["/features/ai-image", "/features/remove-bg", "/use-cases/faceless-youtube-channels"],
    title: "Icon Generator",
    answer:
      "Icon Generator takes a text description of a channel or brand and generates icon and avatar options with AI, exported as high-resolution squares. It is for YouTube channel owners and brand builders who need a distinctive mark without hiring a designer.",
    description:
      "Describe your channel or brand and generate crisp, professional icons and avatars with AI.",
    heroPrefix: "Generate",
    heroHighlight: "custom icons",
    heroSuffix: "with AI",
    heroImage: "/features/icon-generator/icon-generator-hero.png",
    buttonText: "Generate an Icon",
    buttonLink: "/tools/icon-generator",
    seoTitle: "AI Icon Generator",
    seoDescription:
      "Generate professional icons and channel avatars from a text prompt. Perfect for YouTube channels, apps, and brands.",
    faqs: [
      {
        question: "What can I use generated icons for?",
        answer:
          "Channel avatars, app icons, brand marks, thumbnails, anywhere you need a crisp, distinctive mark.",
      },
      {
        question: "Can I generate multiple options?",
        answer:
          "Yes. Each prompt returns variations, so pick a favorite or refine the prompt and go again.",
      },
      {
        question: "What do icons export as?",
        answer: "High-resolution squares that scale cleanly from channel art down to favicon size.",
      },
      {
        question: "Do I own the icons?",
        answer: "Yes. Icons you generate are yours to use, including commercially.",
      },
    ],
  },
  api: {
    related: ["/features/auto-clip", "/features/ai-voiceover", "/use-cases/podcast-clipping"],
    title: "Developer API",
    answer:
      "The Crayo Developer API is a REST interface that runs auto clipping, AI voiceovers, image generation, and short-form video rendering from your own code with one bearer key. It is for developers, agencies, and product teams putting Crayo inside their own apps and pipelines.",
    description:
      "Call Crayo from your own product: auto clipping, AI voiceovers, images, and rendered short-form video over one REST API.",
    heroPrefix: "The",
    heroHighlight: "short-form video API",
    heroSuffix: "for your product",
    heroImage: "/features/api/api-hero.png",
    buttonText: "Get your API key",
    buttonLink: "/developers",
    seoTitle: "AI Video API for Clipping, Voiceovers and Story Videos",
    seoDescription:
      "REST API for auto clipping, AI voiceovers, images and rendered short-form video. One bearer key, 34 endpoints, webhooks on finished jobs.",
    faqs: [
      {
        question: "How do I authenticate with the Crayo API?",
        answer:
          "Create a key in the Crayo developer console and send it as an Authorization: Bearer header on every request. The key is shown once and stored only as a hash, so save it somewhere safe.",
      },
      {
        question: "What does the API cost to use?",
        answer:
          "The API spends the same credits as the app: image, voiceover, export and video. Credits are charged when a request is accepted, and the response tells you how many were charged.",
      },
      {
        question: "Do I need a specific plan to get API access?",
        answer:
          "No plan tier is required. Every Crayo account can create a key, and the API is usable at full capability on any paid plan, since chargeable calls draw on your credit and storage balance.",
      },
      {
        question: "How do long running jobs work?",
        answer:
          "Exports and AutoClip jobs are asynchronous. They return a queued id immediately, then you poll the job endpoint or register a webhook for export.completed, export.failed, autoclip.completed and autoclip.failed.",
      },
      {
        question: "What are the rate limits?",
        answer:
          "Per key, most endpoints allow 2 requests per second and 50 per minute. Heavy generation is 10 per minute, polling is 120 per minute, and uploads are 20 per minute.",
      },
      {
        question: "What can I build with the video API?",
        answer:
          "Clipping pipelines that turn long uploads into vertical shorts, story videos composed scene by scene, generated images and narration, and asset libraries kept in sync through webhooks.",
      },
    ],
  },
  "ai-captions": {
    related: [
      "/features/auto-clip",
      "/features/video-editor",
      "/features/clip-cutter",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-submagic",
      "/compare/crayo-vs-captions-ai",
    ],
    title: "AI Captions",
    answer:
      "AI Captions transcribes the speech in a clip and renders it as animated, word-timed subtitles, with 32 caption templates and transcription in 56 spoken languages. It is for clippers, podcasters, and streamers who want every short captioned for sound-off viewing without typing a word.",
    description:
      "Accurate, animated captions on every clip. Pick a template, highlight keywords, and tweak the style in the editor.",
    heroPrefix: "Add",
    heroHighlight: "AI captions",
    heroSuffix: "to every clip automatically",
    heroImage: "/features/ai-captions/ai-captions-hero.png",
    buttonText: "Caption a Clip Now",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Captions Generator for Shorts, Reels and TikTok",
    seoDescription:
      "Generate animated captions for Shorts, Reels and TikTok with AI. 32 caption templates, 56 spoken languages, keyword highlights and full style control.",
    faqs: [
      {
        question: "Where do the captions get added?",
        answer:
          "Auto Clip and the AI Video Editor caption every clip they produce. In the timeline editor you can also run AI subtitles on any video, or write manual subtitles line by line.",
      },
      {
        question: "Which languages can Crayo transcribe?",
        answer:
          "56 spoken languages, including English, Spanish, Portuguese, French, German, Hindi, Arabic, Japanese and Korean. You pick the spoken language before the job starts so the transcript uses the right model.",
      },
      {
        question: "Can I change how the captions look?",
        answer:
          "Yes. Choose one of 32 templates, then adjust words per line, position, text color, outline, shadow, animation and emphasis. Keyword pop highlights the important words, and edits can apply to one line or all lines.",
      },
      {
        question: "Can I fix a word the transcript got wrong?",
        answer:
          "Yes. Open the clip in the editor, select the caption line and edit the text. The timing and styling stay in place.",
      },
    ],
  },
  "find-viral-moments": {
    related: [
      "/features/auto-clip",
      "/features/clip-cutter",
      "/features/ai-captions",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
    ],
    title: "Find Viral Moments",
    answer:
      "Find Viral Moments scans a podcast, stream, or interview up to 3 hours long, scores the moments most likely to perform, and saves each one as its own clip. It is for clippers and podcast teams who want the shortlist first and the editing after.",
    description:
      "Upload a long video, tell us what is in it, and get a ranked set of moments you can review, trim and keep.",
    heroPrefix: "Find the",
    heroHighlight: "viral moments",
    heroSuffix: "in any long video",
    heroImage: "/features/find-viral-moments/find-viral-moments-hero.png",
    buttonText: "Find Moments Now",
    buttonLink: "/create/find-moments",
    seoTitle: "Find Viral Moments in Long Videos with AI",
    seoDescription:
      "Upload a podcast, stream or interview and let AI find and score the moments worth posting. Review, trim and save up to 50 clips from one video.",
    faqs: [
      {
        question: "What do I control before the search runs?",
        answer:
          "How many moments to find (5 to 50), the moment length (30, 45, 60 or 90 seconds), the spoken language, what kind of content it is (talking, podcast, interview, action or auto detect) and an optional focus note, such as only the parts about pricing.",
      },
      {
        question: "Do I get to check the moments before they are saved?",
        answer:
          "Yes. Turn on Review before saving and the moments open in Clip Cutter, where you keep, skip or trim each one before it becomes a clip. Leave it off and every moment is saved to your Assets as soon as the search finishes.",
      },
      {
        question: "How long can the source video be?",
        answer:
          "Anything from 1 minute to 3 hours. Longer sources simply take more time to scan.",
      },
      {
        question: "What does it cost in credits?",
        answer:
          "One workflow credit per moment found. Moments we do not find are refunded, and you can run up to three searches at once.",
      },
    ],
  },
  "clip-cutter": {
    related: [
      "/features/find-viral-moments",
      "/features/video-cutter",
      "/features/video-editor",
      "/use-cases/clippers",
      "/use-cases/streamers",
      "/compare/crayo-vs-streamladder",
    ],
    title: "Clip Cutter",
    answer:
      "Clip Cutter is a timeline tool that cuts a long video into up to 50 separate clips in one pass, each marked by dragging a range on the track. It is for clippers and streamers who already know which parts they want and need them saved as individual files fast.",
    description:
      "Mark the parts you want on one timeline, name them, and save every range as its own clip.",
    heroPrefix: "Cut",
    heroHighlight: "multiple clips",
    heroSuffix: "from one video in a single pass",
    heroImage: "/features/clip-cutter/clip-cutter-hero.png",
    buttonText: "Cut Clips Now",
    buttonLink: "/create/clip-cutter",
    seoTitle: "Online Clip Cutter: Cut Clips from Any Video",
    seoDescription:
      "Cut up to 50 clips from one long video on a single timeline. Drag ranges, trim the edges, name each clip and save them all as separate files.",
    faqs: [
      {
        question: "How do I mark a clip?",
        answer:
          "Press I to start a clip at the playhead and O to end it, or drag a range on the track. Drag the handles to trim, drag the body to move it, and type exact times like 1:02:03.4 when you need precision.",
      },
      {
        question: "How many clips can I cut from one video?",
        answer:
          "Up to 50 per save, from a source up to 3 hours long. Each clip can run from 1 second to 5 minutes and gets its own name.",
      },
      {
        question: "Does it add captions or effects?",
        answer:
          "No. Clip Cutter saves raw cuts to your Assets. Send them to the AI Video Editor afterwards for speaker framing, captions and a title, or open them in the timeline editor.",
      },
      {
        question: "Are there keyboard shortcuts?",
        answer:
          "Yes. Space plays and pauses, arrow keys step 100 milliseconds, Shift plus arrows step a full second, plus and minus zoom the timeline, Delete removes the selected clip, and undo and redo are one key away.",
      },
    ],
  },
  "ai-reframe": {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/features/video-editor",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
    ],
    title: "AI Reframe",
    answer:
      "AI Reframe turns a 16:9 recording into a 9:16 clip by detecting faces, following whoever is speaking, and moving the crop with smooth, held camera moves instead of jittery tracking. It is for clippers, podcasters, and streamers who need vertical clips where the speaker is always in frame.",
    description:
      "Face detection and speaker tracking keep the talker centered as the crop moves from widescreen to vertical.",
    heroPrefix: "Reframe",
    heroHighlight: "16:9 to 9:16",
    heroSuffix: "with face tracking",
    heroImage: "/features/ai-reframe/ai-reframe-hero.png",
    buttonText: "Reframe a Video Now",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Reframe: 16:9 to 9:16 with Face Tracking",
    seoDescription:
      "Convert widescreen podcasts and streams into vertical clips. AI finds the faces, follows the active speaker and keeps the crop steady between moves.",
    faqs: [
      {
        question: "How does it decide where to crop?",
        answer:
          "A face detection model finds every face in the shot and lip movement tells us who is talking. The crop centers on the active speaker and only glides to a new position when the subject drifts outside a dead zone, so the frame does not jitter on every head movement.",
      },
      {
        question: "What happens with screen recordings or no faces?",
        answer:
          "The planner classifies each clip. Screen recordings, clips with no face to follow, and clips where the only face is a small webcam overlay in a corner keep the whole frame instead of a blurry crop.",
      },
      {
        question: "Which layouts can I choose?",
        answer:
          "Fullscreen puts the speaker in the full vertical frame, Streamer adds a blurred backdrop, Split stacks a second video below, and Letterbox keeps the whole frame with no crop. Output can be 9:16, 1:1 or 4:5.",
      },
      {
        question: "Can I adjust the framing by hand?",
        answer:
          "Yes. Open the clip in the editor, go to Effects then Reframe, and edit the camera keyframes, or lock the camera on one speaker for the whole clip.",
      },
    ],
  },
  "view-tracker": {
    related: [
      "/features/auto-clip",
      "/features/api",
      "/use-cases/clippers",
      "/use-cases/agencies",
      "/calculators/views-to-money",
      "/calculators/posts-to-a-million-views",
    ],
    title: "View Tracker",
    answer:
      "View Tracker records the views, likes, comments and followers of public Instagram, YouTube and TikTok accounts or posts on a schedule you pick, so you can see growth over time and what went viral. It is for clippers reporting campaign views, agencies watching client pages, and creators studying competitors.",
    description:
      "Paste a link to any public account or post. We snapshot it on a schedule and chart what changed.",
    heroPrefix: "Track views across",
    heroHighlight: "TikTok, Reels & Shorts",
    heroSuffix: "",
    heroImage: "/features/view-tracker/view-tracker-hero.png",
    buttonText: "Track an Account Now",
    buttonLink: "/view-tracker",
    seoTitle: "View Tracker for Clippers: TikTok, Reels and Shorts Views",
    seoDescription:
      "Track views on Instagram, YouTube and TikTok accounts or posts from one dashboard. Scheduled snapshots, growth charts, viral alerts and shareable reports.",
    faqs: [
      {
        question: "Do I need to connect my accounts?",
        answer:
          "No. Paste a username, profile link or post link. Only public profiles and posts can be tracked, and it works for your own pages and for anyone you want to study.",
      },
      {
        question: "How often is the data refreshed?",
        answer:
          "You choose the cadence per tracker, from every 10 minutes to once a day. Each refresh costs one tracker credit, and reading what is already stored is free.",
      },
      {
        question: "How many tracker credits do I get?",
        answer:
          `${PURCHASE_PLANS.map((plan, index) => `${plan.name}${index === 0 ? " includes" : ""} ${plan.values.trackerCredits.toLocaleString("en-US")}${index === 0 ? " a month" : ""}`).join(", ")}. Extra packs start at ${TRACKER_PACK_ENTRY.credits} credits for ${money(TRACKER_PACK_ENTRY.price)}, never expire, and we never charge automatically.`,
      },
      {
        question: "What can I do with the numbers?",
        answer:
          "Chart views over time, get alerts on your Discord webhook when a post goes viral or gets removed, export the data, share a report card as an image, and send a viral post straight into Auto Clip. The same data is available through the Crayo API.",
      },
    ],
  },
  "video-editor": {
    related: [
      "/features/auto-clip",
      "/features/ai-captions",
      "/features/ai-reframe",
      "/features/clip-cutter",
      "/compare/crayo-vs-capcut",
      "/compare/crayo-vs-veed",
    ],
    title: "Video Editor",
    answer:
      "The Crayo Video Editor is a browser-based timeline editor for short-form clips with video, caption, text and audio tracks, plus zooms, transitions, LUTs, speed ramps and camera keyframes. It is included on every plan and built for clippers finishing Auto Clip output or editing uploads from scratch.",
    description:
      "A multi-track timeline with captions, zooms, transitions, LUTs, speed ramps, cutaways and camera keyframes. No install, no watermark on any plan.",
    heroPrefix: "The",
    heroHighlight: "AI video editor",
    heroSuffix: "made for short-form clips",
    heroImage: "/features/video-editor/video-editor-hero.png",
    buttonText: "Open the Editor",
    buttonLink: "/projects",
    seoTitle: "AI Video Editor for Short-Form Clips",
    seoDescription:
      "Edit short-form clips in your browser on a multi-track timeline. AI captions, zooms, transitions, LUTs, speed ramps and camera keyframes on every plan.",
    faqs: [
      {
        question: "What is on the timeline?",
        answer:
          "Separate tracks for video, subtitles, text, audio and effects. Split a clip at the playhead, trim the start or end, reorder, and layer cutaways above the main clip while the audio below ducks automatically.",
      },
      {
        question: "Which effects are included?",
        answer:
          "Zooms with timing and easing, transitions such as fade, slide, whip, punch, glitch and flashes, LUT color grades, speed ramps with keyframes, and a Camera and reframe panel for moving the crop over time.",
      },
      {
        question: "How are captions handled?",
        answer:
          "Run AI subtitles on the clip or write manual ones, then pick from 32 templates and set words per line, position, animation, emphasis and keyword pop. Changes can apply to one line or every line.",
      },
      {
        question: "Is the editor on every plan?",
        answer:
          "Yes. Every Crayo plan includes the full editor. Plans differ in the minutes of finished video you can export each month, and effect previews need Chrome or Edge.",
      },
    ],
  },
};
